import { useReducer, useCallback, useRef } from 'react';
import { assetUtils } from '@zen/common-utils';
import { ZenJsUtils } from '@zen/common-app-parts';
import reducer, { ACTIONS } from './searchReducer';

const { getAssetFullId } = assetUtils;
const { validateAsset } = ZenJsUtils;

const initialState = {
  edit: {
    base: {
      value: '',
      valid: undefined,
    },
    quote: {
      value: '',
      valid: undefined,
    },
  },
  base: '',
  quote: '',
  searching: false,
  orders: {},
};

export default function useSearchStore({ dbClient } = {}) {
  const [_state, dispatch] = useReducer(reducer, initialState);
  /** @type {initialState} */
  const state = _state;
  const subscriptionId = useRef(null);

  // actions ---
  const reset = useCallback(() => {
    if (subscriptionId.current) {
      dbClient.unsubscribe(subscriptionId.current);
    }
    dispatch({ type: ACTIONS.RESET.REQUESTED });
  }, [dbClient]);

  const setBase = useCallback((base) => {
    dispatch({ type: ACTIONS.SET_BASE_REQUESTED, payload: base });
  }, []);
  const setQuote = useCallback((quote) => {
    dispatch({ type: ACTIONS.SET_QUOTE_REQUESTED, payload: quote });
  }, []);

  const searchAndSubscribe = useCallback(
    async ({ base, quote }) => {
      const lastBase = state.base;
      const lastQuote = state.quote;
      const searchValid = isSearchValid({ base, quote });

      if (searchValid && hasSearchRepresentationChanged({ base, quote, lastBase, lastQuote })) {
        // update all assets
        dispatch({ type: ACTIONS.UPDATE_ASSETS_REQUESTED, payload: { base, quote } });
      }
      if (!searchValid || !hasSearchChanged({ base, quote, lastBase, lastQuote })) {
        // no need to search
        return;
      }
      dispatch({ type: ACTIONS.SEARCH.REQUESTED });

      // use full asset IDs for the search
      const baseAssetId = getAssetFullId(base);
      const quoteAssetId = getAssetFullId(quote);

      const { items } = await dbClient.findOrdersByBaseQuote({
        base: baseAssetId,
        quote: quoteAssetId,
      });
      dispatch({ type: ACTIONS.SEARCH.SUCCEEDED, payload: items });

      if (subscriptionId.current) {
        dbClient.unsubscribe(subscriptionId.current);
      }

      subscriptionId.current = dbClient.subscribe({
        method: 'findOrdersByBaseQuote',
        payload: { base: baseAssetId, quote: quoteAssetId },
        handler: ({ items }) => {
          dispatch({ type: ACTIONS.SEARCH.SUCCEEDED, payload: items });
        },
      });
    },
    [dbClient, state]
  );
  // --- actions

  return Object.freeze({
    state,
    actions: {
      setBase,
      setQuote,
      search: searchAndSubscribe,
      reset,
    },
  });
}

function isSearchValid({ base, quote }) {
  return (
    validateAsset(base) && validateAsset(quote) && getAssetFullId(base) !== getAssetFullId(quote)
  );
}

function hasSearchChanged({ base, quote, lastBase, lastQuote } = {}) {
  return (
    getAssetFullId(base) !== getAssetFullId(lastBase) ||
    getAssetFullId(quote) !== getAssetFullId(lastQuote)
  );
}

function hasSearchRepresentationChanged({ base, quote, lastBase, lastQuote } = {}) {
  return base !== lastBase || quote !== lastQuote;
}
